import React, { useState, useEffect } from 'react';
import { Wallet, CheckCircle2, Hash, CreditCard, User, Info, History } from 'lucide-react';
import { MultiSelect } from '@/components/common/MultiSelect';
import CommonModal from '@/components/common/CommonModal';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IPendingCashDriver, IPendingCashResponse } from '@/interfaces/CashSettlement/cashSettlement.interface';
import { formatRand, showToastNotification } from '@/utility/helper';
import { DetailItem } from '@/components/common/DetailItem';

interface RecordSettlementModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
    onSeeHistory?: (driverId: string, driverName: string) => void;
}

export const RecordSettlementModal: React.FC<RecordSettlementModalProps> = ({
    open,
    onOpenChange,
    onSuccess,
    onSeeHistory,
}) => {
    const [pendingDrivers, setPendingDrivers] = useState<IPendingCashDriver[]>([]);
    const [selectedDriverId, setSelectedDriverId] = useState<string[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [settling, setSettling] = useState(false);

    const fetchPendingDrivers = async (search = '') => {
        setIsFetching(true);
        try {
            const response = await callApi<IPendingCashResponse>(
                `${API_URL}${API_ENDPOINT.cashSettlement}/pending-driver-collected-cash${search ? `?search=${search}` : ''}`,
                { method: 'GET' }
            );
            setPendingDrivers(response?.data || []);
        } catch (error) {
            console.error("Failed to fetch pending drivers", error);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchPendingDrivers();
            setSelectedDriverId([]);
        }
    }, [open]);

    const selectedDriver = pendingDrivers.find(d => d.driverId === selectedDriverId[0]);

    const handleSettle = async () => {
        if (!selectedDriver) return;
        setSettling(true);
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.cashSettlement}/create`,
                {
                    method: 'POST',
                    data: {
                        driverId: selectedDriver.driverId,
                        totalAmount: Number(selectedDriver.cashAmount),
                        totalOrders: selectedDriver.totalOrders,
                        driverEarning: Number(selectedDriver.driverEarning)
                    }
                }
            );
            showToastNotification('success', 'Cash settlement recorded successfully');
            onSuccess();
            onOpenChange(false);
        } catch (error) {
            ThrowError(error);
        } finally {
            setSettling(false);
        }
    };

    return (
        <CommonModal
            open={open}
            onOpenChange={onOpenChange}
            title="Record Settlement"
            size="md"
        >
            <div className="space-y-6">
                <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <User className="w-4 h-4 text-primary" />
                        Select Driver
                    </label>
                    <MultiSelect
                        options={pendingDrivers.map(d => ({
                            label: `${d.driver?.fullName} — Balance: ${formatRand(Number(d.cashAmount))}`,
                            value: d.driverId,
                            title: `Orders: ${d.totalOrders} | Earning: ${formatRand(Number(d.driverEarning))}`
                        }))}
                        selected={selectedDriverId}
                        onChange={setSelectedDriverId}
                        onSearchChange={fetchPendingDrivers}
                        placeholder={isFetching ? "Loading drivers..." : "Search driver by name..."}
                        singleSelect
                        loading={isFetching}
                    />
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-1">
                        <Info className="w-3 h-3" />
                        Only drivers with pending cash collections are shown.
                    </p>
                </div>

                {selectedDriver ? (
                    <div className="p-4 rounded-2xl bg-primary/5 border border-primary/15 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-bold text-primary flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" /> Settlement Summary
                            </h4>
                            {onSeeHistory && (
                                <button
                                    type="button"
                                    onClick={() => onSeeHistory(selectedDriver.driverId, selectedDriver.driver?.fullName || '')}
                                    className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <History className="w-3.5 h-3.5" /> See History
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <DetailItem label="Total Orders" value={selectedDriver.totalOrders} icon={<Hash />} variant="horizontal" />
                            <DetailItem label="Total Collected" value={formatRand(Number(selectedDriver.cashAmount))} icon={<CreditCard />} variant="horizontal" />
                            <DetailItem label="Driver Earning" value={formatRand(Number(selectedDriver.driverEarning))} icon={<Wallet />} variant="horizontal" />

                            <div className="pt-3 border-t border-dashed mt-2 flex justify-between items-center">
                                <span className="text-xs font-bold uppercase text-muted-foreground">Amount to Settle</span>
                                <span className="text-2xl font-black text-foreground">{formatRand(Number(selectedDriver.cashAmount))}</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-12 text-center border-2 border-dashed border-secondary/10 rounded-2xl">
                        <Wallet className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-20" />
                        <p className="text-sm text-muted-foreground px-10">Select a driver above to review their pending cash collection balance.</p>
                    </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                    <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => onOpenChange(false)}
                        disabled={settling}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="flex-1"
                        onClick={handleSettle}
                        isLoading={settling}
                        disabled={!selectedDriver}
                    >
                        Record Settlement
                    </Button>
                </div>
            </div>
        </CommonModal>
    );
};
